import { AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { isApiError } from '@/lib/api/errors';
import { cn } from '@/lib/utils';

/** Fallback wording for an error that carries nothing worth showing. */
const GENERIC_MESSAGE = 'That could not be loaded. Try again.';

/**
 * Reduce whatever a query rejected with to one displayable sentence.
 *
 * The axios interceptor has already turned every API failure into an `ApiError`
 * whose `message` is English, so this only has to cover the rest: an error
 * thrown inside a `queryFn`, or a non-`Error` value.
 */
export const toDisplayableError = (error: unknown): string => {
  if (isApiError(error)) {
    return error.message;
  }

  if (error instanceof Error && error.message.trim() !== '') {
    return error.message;
  }

  return GENERIC_MESSAGE;
};

/**
 * The error state for every list in the app (spec R6.4, R6.7).
 *
 * Always retryable: the caller passes the query's `refetch`, so a transient
 * failure costs one click rather than a page reload.
 */
export interface ErrorStateProps {
  /** Short headline. Defaults to a neutral one. */
  title?: string;
  /** The thrown value, or an already-written message. */
  error?: unknown;
  /** Retry handler — normally a React Query `refetch`. */
  onRetry?: () => void;
  /** Label for the retry button. */
  retryLabel?: string;
  className?: string;
}

export const ErrorState = ({
  title = 'Could not load this',
  error,
  onRetry,
  retryLabel = 'Try again',
  className,
}: ErrorStateProps) => {
  const message =
    typeof error === 'string' && error.trim() !== ''
      ? error
      : toDisplayableError(error);

  return (
    <Card className={cn('border-destructive/30 shadow-none', className)}>
      <CardContent
        role="alert"
        className="flex flex-col items-center gap-3 px-6 py-14 text-center"
      >
        <AlertTriangle
          aria-hidden="true"
          className="size-6 text-destructive"
          strokeWidth={1.5}
        />
        <p className="text-base font-medium text-foreground">{title}</p>
        <p className="max-w-prose text-sm text-muted-foreground">{message}</p>
        {onRetry ? (
          <Button variant="outline" size="sm" className="mt-2" onClick={onRetry}>
            {retryLabel}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
};
